/**
 * features/cover/useCovers.ts
 * ------------------------------------------------------------------
 * The reads behind cover (lib/cover.ts): the Cover screen, a teacher's My day
 * and School now. Live, so a cover given in the office reaches the teacher's
 * open screen without a reload.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COVERS, COVER_ABSENCES, type CoverAbsence, type CoverRecord } from '@/lib/cover';
import type { ScheduleSettings } from '@/lib/timetable';

function useLive<T>(coll: string, field: string, value: string | number | null): T[] {
  const [rows, setRows] = useState<T[]>([]);
  useEffect(() => {
    if (value == null || value === '') { setRows([]); return; }
    return onSnapshot(
      query(collection(db, coll), where(field, '==', value)),
      (s) => setRows(s.docs.map((d) => ({ ...(d.data() as T), id: d.id }))),
      // A refused or failed read shows no covers; it must not break the screen around it.
      () => setRows([]),
    );
  }, [coll, field, value]);
  return rows;
}

/** Every cover on one date (YYYY-MM-DD). */
export const useCoversOn = (ymd: string | null) => useLive<CoverRecord>(COVERS, 'date', ymd);

/** Every cover in a year: the source of the yearly count. */
export const useCoversInYear = (year: number | null) => useLive<CoverRecord>(COVERS, 'year', year);

/** Who is away on one date. Cover managers only. */
export const useAbsencesOn = (ymd: string | null) => useLive<CoverAbsence>(COVER_ABSENCES, 'date', ymd);

/** One teacher's covers on one date (both equality filters; no composite index needed). */
export function useMyCovers(teacherId: string | null, ymd: string | null): CoverRecord[] {
  const [rows, setRows] = useState<CoverRecord[]>([]);
  useEffect(() => {
    if (!teacherId || !ymd) { setRows([]); return; }
    return onSnapshot(
      query(collection(db, COVERS), where('coverTeacherId', '==', teacherId), where('date', '==', ymd)),
      (s) => setRows(s.docs.map((d) => ({ ...(d.data() as CoverRecord), id: d.id }))),
      () => setRows([]),
    );
  }, [teacherId, ymd]);
  return rows;
}

/**
 * Every bell schedule, keyed by division (preprimary, juniorprimary,
 * upperprimary, secondary). Cover compares lessons across both parts of the
 * school, which keep different bells.
 */
export function useAllSchedules(): Record<string, ScheduleSettings> {
  const [all, setAll] = useState<Record<string, ScheduleSettings>>({});
  useEffect(() => onSnapshot(
    collection(db, 'timetableSchedules'),
    (s) => {
      const out: Record<string, ScheduleSettings> = {};
      s.forEach((d) => { out[d.id] = d.data() as ScheduleSettings; });
      setAll(out);
    },
    () => setAll({}),
  ), []);
  return all;
}
